import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { TagsProvider } from "./context/TagsContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import ImpersonationBanner from "./components/ImpersonationBanner";
import HomePage from "./pages/HomePage";
import PhotoDetailPage from "./pages/PhotoDetailPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import TagPage from "./pages/TagPage";
import PhotographerPage from "./pages/PhotographerPage";
import PhotographersListPage from "./pages/PhotographersListPage";
import LatestPhotosPage from "./pages/LatestPhotosPage";
import PopularPhotosPage from "./pages/PopularPhotosPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import AuthMagic from "./pages/AuthMagic";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import UserSettingsPage from "./pages/UserSettingsPage";
import AccountPage from "./pages/AccountPage";
import MyLikesPage from "./pages/MyLikesPage";
import MyPendingPage from "./pages/MyPendingPage";
import NotFoundPage from "./pages/NotFoundPage";
import CmsOrNotFound from "./pages/CmsOrNotFound";
import ContactPage from "./pages/ContactPage";
import Footer from "./components/Footer";
import CustomCss from "./components/CustomCss";
import RouteBodyClass from "./components/RouteBodyClass";

// Code-split admin + upload out of the public homepage bundle. These heavy,
// authenticated-only pages are loaded on demand so the initial JS parsed on
// public pages stays small (better TBT/LCP).
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const UploadPage = lazy(() => import("./pages/UploadPage"));

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
        <AuthProvider>
          <TagsProvider>
          <CustomCss />
          <RouteBodyClass />
          <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            {/* Admin routes - no navbar */}
            <Route path="/ad-exp3" element={<AdminLoginPage />} />
            <Route path="/ad-exp3/panel" element={<AdminPage />} />
            
            {/* Regular routes with navbar */}
            <Route path="/*" element={
              <>
                <ImpersonationBanner />
                <Navbar />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  {/* SEO-friendly photo URL: /photos/{id}-{alias} */}
                  <Route path="/photos/:imageId" element={<PhotoDetailPage />} />
                  <Route path="/search" element={<SearchResultsPage />} />
                  {/* SEO-friendly tag URL: /photos-hd/{slug} (+ optional /page/N) */}
                  <Route path="/photos-hd/:slug" element={<TagPage />} />
                  <Route path="/photos-hd/:slug/page/:page" element={<TagPage />} />
                  {/* List of all photographers, sorted by photo count */}
                  <Route path="/photographers-in-mauritius" element={<PhotographersListPage />} />
                  {/* Latest photos, paginated */}
                  <Route path="/photos" element={<LatestPhotosPage />} />
                  <Route path="/photos/page/:page" element={<LatestPhotosPage />} />
                  {/* Popular photos (sorted by views), paginated */}
                  <Route path="/photos/popular" element={<PopularPhotosPage />} />
                  <Route path="/photos/popular/page/:page" element={<PopularPhotosPage />} />
                  {/* SEO-friendly photographer URL: /photos/photographer/{user_id}-{username} (+ optional /page/N) */}
                  <Route path="/photos/photographer/:userSlug" element={<PhotographerPage />} />
                  <Route path="/photos/photographer/:userSlug/page/:page" element={<PhotographerPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auth/magic" element={<AuthMagic />} />
                  <Route
                    path="/complete-profile"
                    element={
                      <ProtectedRoute>
                        <CompleteProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route 
                    path="/upload" 
                    element={
                      <ProtectedRoute>
                        <UploadPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute>
                        <UserSettingsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/account" 
                    element={
                      <ProtectedRoute>
                        <AccountPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/my-likes" 
                    element={
                      <ProtectedRoute>
                        <MyLikesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/my-pending" 
                    element={
                      <ProtectedRoute>
                        <MyPendingPage />
                      </ProtectedRoute>
                    } 
                  />
                  {/* 404 or CMS Page - catch all unmatched routes, check DB first */}
                  <Route path="*" element={<CmsOrNotFound />} />
                </Routes>
                <Footer />
              </>
            } />
          </Routes>
          </Suspense>
          </TagsProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
